// externals
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';

// libraries
import { Alert } from '@interstate/components/Alert';
import { Action } from '@interstate/components/Action';
import { FINANCE } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import * as creditDecisionSelectors from '@makemydeal/dr-dash-store/selectors/creditDecisionSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as creditDecisionActionCreators from '@makemydeal/dr-dash-store/actions/creditDecisionActionCreators';

import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';

// components
import CashDownWithDeferment from '../deferment/CashDownWithDeferment.interstate';
import FinanceTermField from './common/FinanceTermField.interstate';
import RetailPriceField from './common/RetailPriceField.interstate';
import SellRateField from './common/SellRateField.interstate';
import BuyRateField from './common/BuyRateField.interstate';
import PaymentFrequencyField from './common/PaymentFrequencyField.interstate';
import CreditDecisionStatusField from './common/CreditDecisionStatusField.interstate';
import CreditDecisionLenderField from './common/CreditDecisionLenderNameField.interstate';
import DaysToFirstPaymentField from './common/DaysToFirstPaymentField';
import FirstPaymentDayField from './common/FirstPaymentDayField';
import AlertError from './gridTable/components/AlertError';

// styles
import { TermsTab } from './common/Terms.style';
import { FinanceHeader } from './Finance.styles';
import PaymentGridToggle from './common/PaymentGridToggle';
import { ContainerGrid } from '../common/ContainerGrid.style';
import { ContainerGridItem } from '../common/ContainerGridItem';
import { Box } from '@interstate/components/Box';

// utils/constants
import { GENERIC_LAST_FIELD_CHANGED_ERROR } from '../constants';
import type { IPaymentGridErrorState } from '@makemydeal/dr-dash-types';
import { useOrderAgnosticILDPURL } from '../hooks/useOrderAgnosticILDPURL';
import SellingPriceFieldWithDealerDiscounts from './common/SellingPriceField.interstate';
import { setAuthTokenCookie } from '@makemydeal/dr-shared-ui-utils';
import { LockWarning } from '../dealLocking';
import { CashDownField } from './common/CashDownField.interstate';

export type FinanceProps = {
    enableCreditDecisionAlert?: boolean;
    isInsideSlideOut?: boolean;
};

const Finance = ({ enableCreditDecisionAlert = false, isInsideSlideOut = false }: FinanceProps) => {
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const lenderDecisions = useSelector((state: StateTree) => {
        return creditDecisionSelectors.getLenderCreditDecisionsByOfferType(state, currentOfferType);
    });
    const enableCreditBureauAndDecisionPBCRouteOne = useSelector(featureToggleSelectors.enableCreditBureauAndDecisionPBCRouteOne);
    const enableOrderAgnosticILDPUI = useSelector(featureToggleSelectors.enableOrderAgnosticILDPUI);
    const isCreditProviderRouteOne: boolean = useSelector(dealerSelectors.isCreditProviderRouteOne);

    const dispatch = useDispatch();
    const creditDecisionAvailable = lenderDecisions.length > 0;
    const navigateToCreditBureau = () => dispatch(navigationActionCreators.navigateToCreditBureau());
    const navigateToCreditDecision = () => dispatch(navigationActionCreators.navigateToCreditDecision());
    const openCreditDecisionSlideOut = () => dispatch(creditDecisionActionCreators.openCreditDecisionSlideOut());
    const isDeferredDownPaymentEnabled = useSelector(dealerSelectors.isDeferredDownPaymentEnabled);

    const shouldNavigateToCreditDecision =
        !isCreditProviderRouteOne || (enableCreditBureauAndDecisionPBCRouteOne && isCreditProviderRouteOne);

    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );

    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const orderDetailsLink = useSelector(dealXgDetailsSelectors.getOrderDetailsLink);
    const ildpDetailsLink = useSelector(dealXgDetailsSelectors.getIldpDetailsLink);
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const oktaContext = useOktaAuth();
    const getJwtToken = () => oktaContext?.oktaAuth.getAccessToken() || '';
    const orderAgnosticILDPURL = useOrderAgnosticILDPURL();

    const handleViewLenderDecisionClick = () => {
        if (isEcommerceDeal && currentOfferType === FINANCE) {
            const token = getJwtToken();

            const orderLink = ildpDetailsLink || `${orderDetailsLink}?show=ildp`;
            if (ildpDetailsLink) {
                setAuthTokenCookie(token);
            }
            window.open(`${orderLink}`, '_blank');
        } else if (!isEcommerceDeal && enableOrderAgnosticILDPUI && orderAgnosticILDPURL) {
            const token = getJwtToken();
            setAuthTokenCookie(token);
            window.open(`${orderAgnosticILDPURL}`, '_blank');
        } else if (isInsideSlideOut && shouldNavigateToCreditDecision) {
            openCreditDecisionSlideOut();
        } else if (shouldNavigateToCreditDecision) {
            navigateToCreditDecision();
        } else {
            navigateToCreditBureau();
        }
    };

    if (currentOfferType !== FINANCE) return null;

    const financeFields = [
        isDeferredDownPaymentEnabled
            ? { component: <CashDownWithDeferment />, key: 'CashDownWithDeferment' }
            : { component: <CashDownField />, key: 'CashDownField' },
        { component: <CreditDecisionLenderField />, key: 'CreditDecisionLenderField' },
        { component: <CreditDecisionStatusField />, key: 'CreditDecisionStatusField' },
        { component: <FinanceTermField />, key: 'FinanceTermField' },
        { component: <PaymentFrequencyField />, key: 'PaymentFrequencyField' },
        { component: <SellRateField />, key: 'SellRateField' },
        { component: <BuyRateField />, key: 'BuyRateField' },
        { component: <DaysToFirstPaymentField />, key: 'DaysToFirstPaymentField' },
        { component: <FirstPaymentDayField />, key: 'FirstPaymentDayField' }
    ];

    return (
        <TermsTab data-testid="finance-terms">
            <FinanceHeader>
                <Typography data-testid="finance-title" variant="h5">
                    Payment And Lender Information
                </Typography>
                <Action size="sm" data-testid="finance-terms-edit-link" onClick={handleViewLenderDecisionClick}>
                    View Lender Decisions
                </Action>
            </FinanceHeader>
            <Grid xs={12}>
                {enableCreditDecisionAlert && creditDecisionAvailable && (
                    <Alert id="infoAlert" role="infoAlert" type="info" data-testid="terms-lender-decision-alert">
                        Lender decisions are ready for this offer.
                    </Alert>
                )}
            </Grid>
            <Box>
                {paymentGridErrorState.hasError && paymentGridEnabledToggle && (
                    <AlertError message={GENERIC_LAST_FIELD_CHANGED_ERROR} />
                )}
                <LockWarning />
                <Grid xs={12} paddingLeft="0">
                    <PaymentGridToggle currentOfferType={currentOfferType} />
                </Grid>
            </Box>
            <ContainerGrid data-testid="main-finance-content">
                <ContainerGridItem xs={6} alignSelf="baseline" md={paymentGridEnabledToggle ? 6 : 4}>
                    <RetailPriceField />
                </ContainerGridItem>
                <ContainerGridItem xs={6} alignSelf="baseline" md={paymentGridEnabledToggle ? 6 : 4}>
                    <SellingPriceFieldWithDealerDiscounts />
                </ContainerGridItem>
                {!paymentGridEnabledToggle && (
                    <>
                        {financeFields.map(({ key, component }) => (
                            <ContainerGridItem alignSelf="baseline" key={key} xs={6} md={4}>
                                {component}
                            </ContainerGridItem>
                        ))}
                    </>
                )}
            </ContainerGrid>
        </TermsTab>
    );
};

export default Finance;
